/**
 * letterAddress: the address lines of a letter.
 *
 * Converts Land Registry BS7666 format addresses into Royal Mail-compatible
 * postal addresses with standardised address lines.
 *
 * Based on Ordnance Survey AddressBase Technical Specification.
 *
 * ⚠️ THIS IS WHAT IS PRINTED ON AN ENVELOPE. ONE FILE, SHARED BY SERVER AND BROWSER, as
 * models/mailability.js is: `functions/models` is a link to `src/models`, and
 * functions/utils/addressFormatter.js, where this code lived until Oct 2026, is now a one-line
 * door onto this file, so the mailing export calls exactly what it called before.
 *
 * WHY IT MOVED (EST-169, EST-176). The rule for "is this the same home?"
 * (models/sameAddress.js) must read a record the way its letter is printed. Its first version
 * did that with a second implementation of its own, which had drifted from this one in six
 * ways (the review of PR #102). There is one implementation now, and the rule calls it.
 *
 * ⚠️ THE CODE BELOW THIS COMMENT WAS MOVED, NOT REWRITTEN: every line of it is the old file's,
 * in the old order. What it prints is held BYTE FOR BYTE to a recording made before the move:
 * functions/__tests__/letterAddress.test.js (96 shapes of record; in the gate before every
 * functions deploy) and letterAddress.emulator.test.js (the real export over every shape).
 * A change to what a letter prints is its own decision: change the code and the recording in
 * one commit, and say which shapes moved and why.
 *
 * Known, recorded, and not changed by the move: a value that is not text (a building number
 * stored as a number, for one) makes these functions throw; a building NAME that begins with
 * a number is printed on the street's line; a flat that is only in the one-line address is
 * not printed when the record has a number field.
 */

/**
 * Check if a string is empty/null/undefined
 */
const hasValue = (str) => str && str.trim() !== "";

/**
 * Check if a string starts with a number (e.g., "42", "42A", "11-13")
 */
const isNumericStart = (str) => /^[0-9]/.test(str);

/**
 * Check if a string is purely numeric
 */
const isNumeric = (str) => /^\d+$/.test(str);

/**
 * The flat (saon) and the number or name (paon) of a Land Registry record AS THE LETTER USES
 * THEM: trimmed, and with three kinds of badly-filled record put right. Taken out of
 * formatLandRegistryAddress, unchanged, so that the rule for "the same home"
 * (models/sameAddress.js) reads exactly what is printed and repairs nothing for itself.
 *
 * @param {Object} record - Land Registry record
 * @returns {{ saon: string, paon: string }}
 */
function landRegistryParts(record) {
  let saon = (record.saon || "").trim();
  let paon = (record.paon || "").trim();

  // Special case: Land Registry sometimes combines building name and number in saon
  // e.g., saon="NEW MARSHALL HOUSE, 386" with empty paon
  // We should split this into saon="NEW MARSHALL HOUSE" and paon="386"
  if (hasValue(saon) && !hasValue(paon) && saon.includes(",")) {
    const parts = saon.split(",").map((p) => p.trim());
    const lastPart = parts[parts.length - 1];

    // Check if the last part is a number (the house number)
    if (isNumericStart(lastPart)) {
      // Split: everything before the number is the building name, number goes to paon
      paon = lastPart;
      saon = parts.slice(0, -1).join(", ");
    }
  }

  // Special case: If saon has just a house number and paon is empty,
  // Land Registry sometimes puts the primary identifier in saon
  // Check if saon looks like a house number and paon is empty
  if (
    hasValue(saon) &&
    !hasValue(paon) &&
    isNumericStart(saon) &&
    !saon.includes(" ")
  ) {
    // Move saon to paon for correct handling (only if it's just a number, not "FLAT 3" etc)
    paon = saon;
    saon = "";
  }

  // Special case: SAON and PAON are swapped (Land Registry data quality issue)
  // e.g., saon="26" and paon="FLAT 2" should be saon="FLAT 2" and paon="26"
  // Detect: SAON is numeric AND PAON starts with flat/apartment/unit keywords
  if (hasValue(saon) && hasValue(paon) && isNumericStart(saon)) {
    const paonUpper = paon.toUpperCase();
    const flatKeywords = ["FLAT", "APARTMENT", "APT", "UNIT", "STUDIO"];

    if (flatKeywords.some((keyword) => paonUpper.startsWith(keyword))) {
      // Swap them to correct BS7666 format
      const temp = saon;
      saon = paon;
      paon = temp;
    }
  }

  return { saon, paon };
}

/**
 * Format a Land Registry record into structured address lines
 *
 * @param {Object} record - Land Registry record with saon, paon, street, locality, town, county
 * @param {string} postcode - The postcode (usually stored separately)
 * @returns {Object} - { address1, address2, address3, town, postcode, lines[] }
 */
function formatLandRegistryAddress(record, postcode) {
  if (!record) {
    return {
      address1: "",
      address2: "",
      address3: "",
      town: "",
      postcode: postcode || "",
      lines: [],
    };
  }

  const lines = [];

  // Clean and extract fields
  const { saon, paon } = landRegistryParts(record);
  const street = (record.street || "").trim();
  const locality = (record.locality || "").trim();
  const town = (record.town || "").trim();
  const county = (record.county || "").trim();
  const pc = (postcode || "").trim().toUpperCase();

  // Build address lines following BS7666/Royal Mail rules

  if (hasValue(saon)) {
    // Special case: SAON is a number range (e.g., "90 - 92") with no PAON
    // Treat like a house number and combine with street
    if (!hasValue(paon) && isNumericStart(saon) && hasValue(street)) {
      // Number range SAON: combine with street "90 - 92 DERBY ROAD"
      lines.push(`${saon} ${street}`);
    } else {
      // Normal SAON: goes on Line 1
      lines.push(saon);

      if (hasValue(paon)) {
        // PAON also present
        if (isNumericStart(paon) && hasValue(street)) {
          // Numeric PAON: combine with street "145 PARK ROAD"
          lines.push(`${paon} ${street}`);
        } else if (hasValue(street)) {
          // Named PAON: "ROSE COURT, HIGH STREET"
          lines.push(`${paon}, ${street}`);
        } else {
          lines.push(paon);
        }
      } else if (hasValue(street)) {
        // No PAON, just street
        lines.push(street);
      }
    }
  } else if (hasValue(paon)) {
    // No SAON, but PAON present
    if (isNumericStart(paon) && hasValue(street)) {
      // Numeric PAON: combine with street "42 HIGH STREET"
      lines.push(`${paon} ${street}`);
    } else {
      // Named PAON: separate line
      lines.push(paon);
      if (hasValue(street)) {
        lines.push(street);
      }
    }
  } else if (hasValue(street)) {
    // No SAON or PAON, just street
    lines.push(street);
  }

  // Edge case: Rural address without street name
  // If we have a single numeric line and locality, combine them
  // Example: "18" + "BARNWELL" = "18 BARNWELL" (village numbering)
  let skipLocalityLine = false;
  if (lines.length === 1 && isNumeric(lines[0]) && hasValue(locality)) {
    lines[0] = `${lines[0]} ${locality}`;
    skipLocalityLine = true;
  }

  // Add locality if present and different from town (and not already combined above)
  if (
    !skipLocalityLine &&
    hasValue(locality) &&
    locality.toUpperCase() !== town.toUpperCase()
  ) {
    lines.push(locality);
  }

  // Add town
  if (hasValue(town)) {
    lines.push(town);
  }

  // Note: We typically don't include county in modern postal addresses
  // but it's available if needed

  // Build result object
  // For mailing labels we want: address1, address2, address3, town, postcode
  // Where town is the POST TOWN (required by Royal Mail)

  // Find where town is in the lines array
  const townIndex = lines.findIndex(
    (line) => line.toUpperCase() === town.toUpperCase(),
  );

  // Everything before town goes into address1, address2, address3
  const addressLines = townIndex >= 0 ? lines.slice(0, townIndex) : lines;

  return {
    address1: addressLines[0] || "",
    address2: addressLines[1] || "",
    address3: addressLines[2] || "",
    town: town || "",
    postcode: pc,
    lines: lines, // Full array for debugging/flexibility
  };
}

/**
 * Format a property document into structured address lines
 * Uses Land Registry data if available, falls back to other fields
 *
 * @param {Object} property - Property document from Firestore
 * @returns {Object} - { address1, address2, address3, town, postcode, lines[] }
 */
function formatPropertyAddress(property) {
  if (!property) {
    return {
      address1: "",
      address2: "",
      address3: "",
      town: "",
      postcode: "",
      lines: [],
    };
  }

  const postcode = (property.postcode || "").trim();

  // Prefer Land Registry data if available
  if (property.landRegistryRecord) {
    return formatLandRegistryAddress(property.landRegistryRecord, postcode);
  }

  // Fall back to property-level fields
  const lines = [];

  // Try to use structured fields if available
  const saon = (property.saon || "").trim();
  let paon = (property.paon || "").trim();
  const buildingNumber = (property.buildingNumber || "").trim();
  const buildingName = (property.buildingName || "").trim();
  const street = (property.street || property.streetName || "").trim();
  const locality = (property.locality || "").trim();
  const town = (property.town || property.district || "").trim();

  // Use buildingNumber/buildingName as fallback for paon
  if (!hasValue(paon)) {
    paon = buildingNumber || buildingName || "";
  }

  // If we have structured fields, use them
  if (hasValue(saon) || hasValue(paon) || hasValue(street)) {
    // Same logic as Land Registry formatting
    if (hasValue(saon)) {
      lines.push(saon);
      if (hasValue(paon)) {
        if (isNumericStart(paon) && hasValue(street)) {
          lines.push(`${paon} ${street}`);
        } else if (hasValue(street)) {
          lines.push(`${paon}, ${street}`);
        } else {
          lines.push(paon);
        }
      } else if (hasValue(street)) {
        lines.push(street);
      }
    } else if (hasValue(paon)) {
      if (isNumericStart(paon) && hasValue(street)) {
        lines.push(`${paon} ${street}`);
      } else {
        lines.push(paon);
        if (hasValue(street)) {
          lines.push(street);
        }
      }
    } else if (hasValue(street)) {
      lines.push(street);
    }

    // Edge case: Rural address without street name (same as Land Registry path)
    let skipLocalityLine = false;
    if (lines.length === 1 && isNumeric(lines[0]) && hasValue(locality)) {
      lines[0] = `${lines[0]} ${locality}`;
      skipLocalityLine = true;
    }

    if (
      !skipLocalityLine &&
      hasValue(locality) &&
      locality.toUpperCase() !== town.toUpperCase()
    ) {
      lines.push(locality);
    }

    if (hasValue(town)) {
      lines.push(town);
    }
  } else if (hasValue(property.address)) {
    // Last resort: parse the address string
    return parseAddressString(property.address, postcode, town);
  }

  const townIndex = lines.findIndex(
    (line) => line.toUpperCase() === town.toUpperCase(),
  );
  const addressLines = townIndex >= 0 ? lines.slice(0, townIndex) : lines;

  return {
    address1: (addressLines[0] || "").toUpperCase(),
    address2: (addressLines[1] || "").toUpperCase(),
    address3: (addressLines[2] || "").toUpperCase(),
    town: (town || "").toUpperCase(),
    postcode: postcode.toUpperCase(),
    lines: lines,
  };
}

/**
 * Parse an address string into structured lines
 * Used as fallback when no structured data is available
 *
 * @param {string} addressStr - Full address string
 * @param {string} postcode - Postcode
 * @param {string} knownTown - Known town name (if available)
 * @returns {Object} - { address1, address2, address3, town, postcode, lines[] }
 */
function parseAddressString(addressStr, postcode, knownTown = "") {
  if (!addressStr) {
    return {
      address1: "",
      address2: "",
      address3: "",
      town: knownTown || "",
      postcode: (postcode || "").toUpperCase(),
      lines: [],
    };
  }

  // Split by comma and clean up
  const parts = addressStr
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p && p.length > 0);

  // Remove postcode from parts if present
  const pc = (postcode || "").trim().toUpperCase();
  const filteredParts = parts.filter(
    (p) => p.toUpperCase().replace(/\s/g, "") !== pc.replace(/\s/g, ""),
  );

  // Try to identify the town (usually second-to-last or matches knownTown)
  let townIndex = -1;
  let town = "";
  
  if (hasValue(knownTown)) {
    // If we have a known town, check if it appears in the address string
    townIndex = filteredParts.findIndex(
      (p) => p.toUpperCase() === knownTown.toUpperCase(),
    );
    
    // Use the known town regardless of whether it was found in the address string
    town = knownTown;
  } else if (filteredParts.length > 1) {
    // No known town provided: assume last part is town
    townIndex = filteredParts.length - 1;
    town = filteredParts[townIndex];
  }

  // Address lines are everything except the town (if town was found in the string)
  const addressLines =
    townIndex >= 0 ? filteredParts.slice(0, townIndex) : filteredParts;

  return {
    address1: (addressLines[0] || "").toUpperCase(),
    address2: (addressLines[1] || "").toUpperCase(),
    address3: (addressLines[2] || "").toUpperCase(),
    town: town.toUpperCase(),
    postcode: pc,
    lines: [...addressLines, town].filter((l) => l),
  };
}

/**
 * Every field of a property record that formatPropertyAddress reads, and every field of a
 * Land Registry record that formatLandRegistryAddress reads. A function that fetches chosen
 * fields and then prints or compares addresses must fetch all of the first list, or a record
 * silently prints as less than it is. functions/__tests__/letterAddressParts.test.js reads
 * this file's own source and fails if a field is read that is not listed, or listed and not read.
 */
const LETTER_FIELDS = Object.freeze(["landRegistryRecord", "postcode", "saon", "paon", "buildingNumber", "buildingName", "street", "streetName", "locality", "town", "district", "address"]);
const LAND_REGISTRY_FIELDS = Object.freeze(["saon", "paon", "street", "locality", "town", "county"]);

export {
  formatLandRegistryAddress,
  formatPropertyAddress,
  parseAddressString,
  hasValue,
  isNumericStart,
  isNumeric,
  landRegistryParts,
  LETTER_FIELDS,
  LAND_REGISTRY_FIELDS,
};
